import { DerbyRngV1, hashFnv1a } from '../rng';
import type { HorseStats, StatId } from '../race/types';
import {
  expressedPotential,
  foundationGenotype,
  inheritGenotype,
  randomGenotype,
  type Genotype,
} from './genetics';
import type { Horse, HorseId, YearlingLot } from './types';

/** XP needed to go from `level` to `level + 1`. */
export function xpToNext(level: number): number {
  return 20 + 10 * level;
}

export const STARTER_STAT_LEVEL = 3;
export const SEASON_DAYS = 12;
export const FORCED_RETIREMENT_AGE = 8;

export function seasonOf(day: number): number {
  return Math.floor((day - 1) / SEASON_DAYS) + 1;
}

/** Day within the current season, 1..SEASON_DAYS. */
export function seasonDayOf(day: number): number {
  return ((day - 1) % SEASON_DAYS) + 1;
}

export function ageOf(horse: Horse, day: number): number {
  return seasonOf(day) - horse.bornSeason;
}

/** Debut at 2, peak at 4–5, decline into forced retirement at 8. */
export function ageFactor(age: number): number {
  if (age <= 2) return 0.9;
  if (age === 3) return 0.97;
  if (age <= 5) return 1;
  if (age === 6) return 0.94;
  return 0.86;
}

/** What age and potential allow, before the facility cap is applied. */
export function agePotentialCap(horse: Horse, stat: StatId, day: number): number {
  return Math.max(1, Math.round(horse.potential[stat] * ageFactor(ageOf(horse, day))));
}

const HORSE_NAMES = [
  'Copper Penny',
  'Second Sunrise',
  'Borrowed Time',
  'Patchwork Jack',
  'June Bug',
  'Slow Lightning',
  'Handshake Deal',
  'Wild Bramble',
  'Paper Lantern',
  'Nickel Tour',
  'Late Frost',
  'Cartwheel',
  'Pocket Watch',
  'Firm Handshake',
  'Dandelion Wine',
  'North Paddock',
] as const;

/** Deterministic per seed — no RNG state consumed. */
export function starterName(seed: number): string {
  return HORSE_NAMES[(seed >>> 0) % HORSE_NAMES.length]!;
}

export const MAX_NAME_LENGTH = 24;

export function sanitizeName(raw: string): string | null {
  const trimmed = raw.trim().slice(0, MAX_NAME_LENGTH);
  return trimmed.length > 0 ? trimmed : null;
}

export function newCareer(): Horse['career'] {
  return { starts: 0, winsByLeague: [0, 0, 0, 0], earningsCents: 0 };
}

/** Shared construction: every horse enters the world through here. */
export function horseFromGenotype(
  id: HorseId,
  name: string,
  sex: Horse['sex'],
  bornSeason: number,
  genotype: Genotype,
  options: {
    stats?: HorseStats;
    location?: Horse['location'];
    sireId?: HorseId | null;
    damId?: HorseId | null;
    retired?: boolean;
  } = {},
): Horse {
  const potential = expressedPotential(genotype);
  const stats: HorseStats = options.stats ?? {
    speed: Math.max(1, Math.round(0.15 * potential.speed)),
    stamina: Math.max(1, Math.round(0.15 * potential.stamina)),
    accel: Math.max(1, Math.round(0.15 * potential.accel)),
    heart: Math.max(1, Math.round(0.15 * potential.heart)),
  };
  return {
    id,
    name,
    sex,
    bornSeason,
    stats,
    xp: { speed: 0, stamina: 0, accel: 0, heart: 0 },
    potential,
    genotype,
    sireId: options.sireId ?? null,
    damId: options.damId ?? null,
    revealedGrades: [],
    revealedTraits: [],
    masteredTrainingModes: [],
    bredThisSeason: false,
    fatigue: 0,
    injury: null,
    injuriesEver: 0,
    career: newCareer(),
    location: options.location ?? 'barn',
    retired: options.retired ?? false,
  };
}

/** Conception is deterministic per (world, season, pairing). */
export function conceiveFoal(
  worldSeed: number,
  season: number,
  sire: Horse,
  dam: Horse,
  id: HorseId,
): Horse {
  const rng = new DerbyRngV1(hashFnv1a(`${worldSeed}:foal:${season}:${dam.id}:${sire.id}`));
  const genotype = inheritGenotype(sire.genotype, dam.genotype, rng);
  const name = rng.pick(FOAL_NAMES);
  return horseFromGenotype(id, name, rng.chance(0.5) ? 'M' : 'F', season, genotype, {
    location: 'barn',
    sireId: sire.id,
    damId: dam.id,
  });
}

const FOAL_NAMES = [
  'Little Ember',
  'New Money',
  'Early Edition',
  'Spring Ledger',
  'Half Note',
  'Bright Penny',
  'Morning Mail',
  'Second Verse',
  'Small Hours',
  'First Frost',
] as const;

const STUD_NAMES = [
  'Ironwood Sky',
  'Court Recorder',
  'Golden Hour',
  'Thunderhead',
  'Silent Auction',
  'Riverboat King',
  'Northern Ledger',
  'Midas Whisper',
] as const;

/** The eight stallions standing at stud: fixed negative ids so pedigree
 * references survive every prune, quality rising in tier pairs. */
export function makeStuds(worldSeed: number): Horse[] {
  const rng = new DerbyRngV1(hashFnv1a(`${worldSeed}:studs`));
  const tierMeans = [7, 7, 11, 11, 15, 15, 18, 18];
  return tierMeans.map((mean, index) => {
    const genotype = randomGenotype(rng, mean, 3);
    const stud = horseFromGenotype(-(index + 1), STUD_NAMES[index]!, 'M', -10, genotype, {
      location: 'external',
      retired: true,
    });
    // Studs raced in their day: trained to their potential.
    stud.stats = { ...stud.potential };
    return stud;
  });
}

export function studFeeCents(stud: Horse): number {
  const avg =
    (stud.potential.speed + stud.potential.stamina + stud.potential.accel + stud.potential.heart) / 4;
  return 20_000 + 6_000 * Math.max(0, Math.round(avg) - 8);
}

/** Rough market worth — replaced by pedigree valuation in phase 4B. */
export function horseValueCents(horse: Horse, day: number): number {
  const statSum =
    horse.stats.speed + horse.stats.stamina + horse.stats.accel + horse.stats.heart;
  const potentialSum =
    horse.potential.speed + horse.potential.stamina + horse.potential.accel + horse.potential.heart;
  let value =
    20_000 +
    1_200 * statSum +
    500 * potentialSum +
    15_000 * horse.revealedTraits.length +
    Math.round(horse.career.earningsCents * 0.02);
  if (horse.retired || ageOf(horse, day) >= 7) value = Math.round(value * 0.6);
  return Math.max(10_000, value);
}

export function yearlingPriceCents(lot: YearlingLot): number {
  const potentialAvg =
    (lot.potential.speed + lot.potential.stamina + lot.potential.accel + lot.potential.heart) / 4;
  return 30_000 + 2_500 * Math.max(0, Math.round(potentialAvg) - 8);
}

/**
 * The season's yearling stock: two youngsters, quality scaling with how
 * far the stable has climbed. Deterministic per (seed, season) — no sim
 * RNG consumed. In phase 4B this becomes the mare auction.
 */
export function rollYearlings(
  worldSeed: number,
  season: number,
  bestLeagueUnlocked: number,
): YearlingLot[] {
  const rng = new DerbyRngV1(hashFnv1a(`${worldSeed}:yearlings:${season}`));
  const lots: YearlingLot[] = [];
  const usedNames = new Set<string>();
  for (let index = 0; index < 2; index += 1) {
    const draw = (): number =>
      Math.max(6, Math.min(20, 10 + bestLeagueUnlocked * 2 + rng.integer(-4, 4)));
    const potential: Record<StatId, number> = {
      speed: draw(),
      stamina: draw(),
      accel: draw(),
      heart: draw(),
    };
    let name = rng.pick(HORSE_NAMES);
    while (usedNames.has(name)) name = rng.pick(HORSE_NAMES);
    usedNames.add(name);
    // Potential now flows from real genes; the old direct draw sets the
    // quality band the genes are rolled around.
    const targetMean = Math.round((potential.speed + potential.stamina + potential.accel + potential.heart) / 4);
    const genotype = randomGenotype(rng, targetMean, 4);
    const truePotential = expressedPotential(genotype);
    const estimates = {} as Record<StatId, number>;
    for (const stat of ['speed', 'stamina', 'accel', 'heart'] as StatId[]) {
      estimates[stat] = Math.max(1, Math.min(20, truePotential[stat] + rng.integer(-2, 2)));
    }
    const lot: YearlingLot = {
      lotId: season * 10 + index,
      name,
      sex: rng.chance(0.5) ? 'M' : 'F',
      stats: {
        speed: rng.integer(2, 3),
        stamina: rng.integer(2, 3),
        accel: rng.integer(2, 3),
        heart: rng.integer(2, 3),
      },
      potential: truePotential,
      estimates,
      genotype,
      priceCents: 0,
    };
    lot.priceCents = yearlingPriceCents(lot);
    lots.push(lot);
  }
  return lots;
}

export function foundationHorse(id: number, name: string, bornSeason: number, worldSeed: number): Horse {
  const rng = new DerbyRngV1(hashFnv1a(`${worldSeed}:foundation:${id}`));
  const genotype = foundationGenotype({ speed: 20, stamina: 20, accel: 20, heart: 20 }, rng);
  return horseFromGenotype(id, name, 'M', bornSeason, genotype, {
    stats: {
      speed: STARTER_STAT_LEVEL,
      stamina: STARTER_STAT_LEVEL,
      accel: STARTER_STAT_LEVEL,
      heart: STARTER_STAT_LEVEL,
    },
    location: 'featured',
  });
}
